import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import arrayMutators from 'final-form-arrays';
import MakeAsyncFunction from 'react-redux-promise-listener';
import { Field as FField, Form as FForm } from 'react-final-form';
import { FieldArray as FFieldArray } from 'react-final-form-arrays';
import { Row, Col, Empty, Space, Form as AForm, Input, InputNumber, Button, Typography } from 'antd';
import { PlusOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { dispatch, promiseListener } from '../../../../store';
import { EditableTitleControl } from '../../../fields/Fields';

import {
  CREATE_QUESTION_SET_FAILURE,
  CREATE_QUESTION_SET_REQUEST,
  CREATE_QUESTION_SET_SUCCESS,
  handleFetchGeneratedQuestionSetRequest,
  handleClearGeneratedQuestionSet
} from '../../../../modules/question-set/redux';
import { useGeneratedQuestionSet } from '../../../../modules/question-set/hooks';
import QuestionSetIcon from '../../../../assets/question-set-image.svg';
import { QuestionItem } from './QuestionItem';
import {
  StyledContainer,
  AddQuestionButton,
  DoneButton,
  StyledButton
} from '../../../../styles/StyledComponents';
import LoadingSpinner from '../../../../lib/components/loadingSpinner';
import { handleShowToast } from '../../../../modules/notification/redux';
import { FORM_ERROR } from 'final-form';
import { mapGeminiQuestionToFormQuestion } from './geminiQuestionMapper';

const { Text } = Typography;

function isTreatAsBlankQuestion (question) {
  if (!question) return true;
  if (question.questionFormat === 'free-response') return false;
  const stmt = String(question.questionStatement || '').trim();
  const numOfPossibleAnswers = Array.isArray(question.possibleAnswers) ? question.possibleAnswers.length : 0;
  return !stmt && numOfPossibleAnswers === 0;
}

function GenerateQuestionsPanel ({ form, questions, generatedQuestionSet, isGenerating, generatedQuestionSetError }) {
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiCount, setAiCount] = useState(3);
  const lastProcessedRef = useRef(null);

  const maxToGenerate = useMemo(() => {
    const list = questions || [];
    if (list.length === 1 && isTreatAsBlankQuestion(list[0])) {
      return 15;
    }
    return Math.max(0, 15 - list.length);
  }, [questions]);

  useEffect(() => {
    if (!generatedQuestionSet || generatedQuestionSet === lastProcessedRef.current) return;
    lastProcessedRef.current = generatedQuestionSet;

    const raw = generatedQuestionSet?.questions || [];
    const mapped = raw.map(mapGeminiQuestionToFormQuestion).filter(Boolean);
    if (!mapped.length) {
      dispatch(handleShowToast({ message: 'No valid questions were returned. Try a different prompt.', type: 'warning' }));
      return;
    }
    const incomingGeminiQuestions = mapped.slice(0, maxToGenerate);
    const existing = form.getState().values.questions || [];
    let updatedQuestionSet;
    if (existing.length === 1 && isTreatAsBlankQuestion(existing[0])) {
      updatedQuestionSet = incomingGeminiQuestions;
    } else {
      updatedQuestionSet = [...existing, ...incomingGeminiQuestions].slice(0, 15);
    }
    form.batch(() => {
      form.change('questions', updatedQuestionSet);
    });
    dispatch(handleShowToast({
      message: `Added ${incomingGeminiQuestions.length} question${incomingGeminiQuestions.length === 1 ? '' : 's'} from AI.`,
      type: 'success'
    }));
  }, [generatedQuestionSet, form, maxToGenerate]);

  useEffect(() => {
    if (!generatedQuestionSetError) return;
    dispatch(handleShowToast({ message: generatedQuestionSetError, type: 'error' }));
  }, [generatedQuestionSetError]);

  const handleGenerate = (eventOnClickGenerateButton) => {
    eventOnClickGenerateButton.preventDefault();
    eventOnClickGenerateButton.stopPropagation();
    const prompt = aiPrompt.trim();
    if (!prompt || isGenerating || maxToGenerate <= 0) return;

    const numOfQuestionsToGenerate = Math.min(Math.max(1, Number(aiCount) || 1), maxToGenerate);
    const fullPrompt = `${prompt}\n\nGenerate exactly ${numOfQuestionsToGenerate} assessment questions. Use only "multiple-choice" or "true-false" for questionFormat. For multiple-choice, include exactly one correct answer and several incorrect options (typically four options total). Each incorrect option must include a non-empty hint string in its hint field.`;
    dispatch(handleFetchGeneratedQuestionSetRequest({ prompt: fullPrompt }));
  };

  const disabled = maxToGenerate <= 0;

  return (
    <Row gutter={[16, 16]} style={{ marginTop: 4, marginBottom: 8 }}>
      <Col span={24}>
        <Text type='secondary'>
          Describe a topic and Gemini will draft multiple-choice or true/false questions. You can edit them before saving.
        </Text>
      </Col>
      <Col xs={24} md={14}>
        <Input.TextArea
          value={aiPrompt}
          onChange={(eventOnChangeAiPromptTextArea) => setAiPrompt(eventOnChangeAiPromptTextArea.target.value)}
          placeholder='e.g. Photosynthesis for 7th grade — mix of MC and true/false'
          rows={3}
          maxLength={7500}
          disabled={disabled || isGenerating}
        />
      </Col>
      <Col xs={24} md={10}>
        <Space direction='vertical' style={{ width: '100%' }}>
          <Space wrap>
            <Text>How many</Text>
            <InputNumber
              min={1}
              max={Math.max(1, maxToGenerate)}
              value={aiCount}
              onChange={(aiCountValue) => setAiCount(aiCountValue == null ? 1 : aiCountValue)}
              disabled={disabled || isGenerating}
            />
          </Space>
          <Button
            type='default'
            icon={<ThunderboltOutlined />}
            loading={isGenerating}
            disabled={disabled || !aiPrompt.trim()}
            onClick={handleGenerate}
          >
            Generate questions
          </Button>
          {disabled && (
            <Text type='secondary'>Remove questions or clear the set to generate more (max 15).</Text>
          )}
        </Space>
      </Col>
    </Row>
  );
}

GenerateQuestionsPanel.propTypes = {
  form: PropTypes.shape({
    batch: PropTypes.func.isRequired,
    change: PropTypes.func.isRequired,
    getState: PropTypes.func.isRequired
  }).isRequired,
  questions: PropTypes.arrayOf(PropTypes.object),
  generatedQuestionSet: PropTypes.object,
  isGenerating: PropTypes.bool,
  generatedQuestionSetError: PropTypes.string
};

GenerateQuestionsPanel.defaultProps = {
  questions: [],
  generatedQuestionSet: null,
  isGenerating: false,
  generatedQuestionSetError: null
};

export const CreateQuestionSetForm = ({ isLoading }) => {
  const { generatedQuestionSet, isGenerating, generatedQuestionSetError } = useGeneratedQuestionSet();

  useEffect(() => {
    dispatch(handleClearGeneratedQuestionSet());
    return () => {
      dispatch(handleClearGeneratedQuestionSet());
    };
  }, []);

  const initialValues = useMemo(() => ({
    title: 'New Question Set',
    questions: [{ questionFormat: 'multiple-choice', questionDifficulty: 3 }]
  }), []);

  return (
    <MakeAsyncFunction
      listener={promiseListener}
      start={CREATE_QUESTION_SET_REQUEST}
      resolve={CREATE_QUESTION_SET_SUCCESS}
      reject={CREATE_QUESTION_SET_FAILURE}
      getError={(action) => {
        const payload = action.payload;
        const error = payload[FORM_ERROR] ? payload[FORM_ERROR] : 'something went wrong';
        dispatch(handleShowToast({ message: error, type: 'error' }));
      }}
    >
      {(onSubmit) => (
        <FForm
          name='create-question-set'
          initialValues={initialValues}
          mutators={{
            ...arrayMutators
          }}
          onSubmit={onSubmit}
          render={(renderProps) => {
            const {
              handleSubmit,
              form,
              submitting,
              hasValidationErrors,
              hasSubmitErrors,
              dirtySinceLastSubmit,
              values
            } = renderProps;
            const { mutators: { push, removeBatch } } = form;
            return (
              <div>
                <LoadingSpinner spinning={isLoading}>
                  <AForm onFinish={handleSubmit}>
                    <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 16]}>
                      <Col style={{ width: '100%' }}>
                        <FField
                          name='title'
                          component={EditableTitleControl}
                          tooltip='What is a Molecule?'
                          level={3}
                          // validate={validations.required}
                        />
                        {values.questions.length}/15 questions added
                      </Col>
                    </Row>
                    <GenerateQuestionsPanel
                      form={form}
                      questions={values.questions}
                      generatedQuestionSet={generatedQuestionSet}
                      isGenerating={isGenerating}
                      generatedQuestionSetError={generatedQuestionSetError}
                    />
                    <Space direction='vertical' style={{ width: '100%' }}>
                      <FFieldArray name='questions'>
                        {({ fields, meta }) =>
                          fields.map((name, index) => (
                            <StyledContainer
                              key={name}
                              style={{
                                marginTop: 20,
                                width: '100%',
                                padding: '40px 50px '
                              }}
                            >
                              <QuestionItem
                                name={name}
                                index={index}
                                push={push}
                                numOfQuestions={fields.length}
                                remove={fields.remove}
                                removeBatch={removeBatch}
                              />
                            </StyledContainer>
                          ))}
                      </FFieldArray>
                      {values.questions.length > 0 &&
                    values.questions.length < 15 && (
                        <Row gutter={[16, 16]} justify='center'>
                          <Col span={12}>
                            <AddQuestionButton
                              block
                              type='dashed'
                              style={{}}
                              onClick={(eventOnClickAddQuestionButton) => {
                                eventOnClickAddQuestionButton.preventDefault();
                                eventOnClickAddQuestionButton.stopPropagation();
                                push('questions', null);
                              }}
                            >
                              <PlusOutlined /> Question
                            </AddQuestionButton>
                          </Col>
                          <Col span={12}>
                            <DoneButton
                              type='primary'
                              htmlType='submit'
                              disabled={
                                submitting ||
                              ((hasSubmitErrors || hasValidationErrors) &&
                                !dirtySinceLastSubmit)
                              }
                              loading={isLoading || submitting}
                              block
                            >
                            Done!
                            </DoneButton>
                          </Col>
                        </Row>
                      )}
                      {values.questions.length === 15 && (
                        <div>
                          <DoneButton
                            type='primary'
                            htmlType='submit'
                            disabled={
                              submitting ||
                          ((hasSubmitErrors || hasValidationErrors) &&
                            !dirtySinceLastSubmit)
                            }
                            loading={isLoading || submitting}
                            block
                          >
                        Done!
                          </DoneButton>
                        </div>
                      )}
                    </Space>
                    <div>
                      {values.questions.length === 0 && (
                        <Empty
                          image={QuestionSetIcon}
                          imageStyle={{ height: 90 }}
                          description=''
                        >
                          <AForm.Item>
                            <StyledButton
                              type='primary'
                              onClick={(eventOnClickCreateFirstQuestionButton) => {
                                eventOnClickCreateFirstQuestionButton.preventDefault();
                                eventOnClickCreateFirstQuestionButton.stopPropagation();
                                push('questions', null);
                              }}
                            >
                          Create my first question!
                            </StyledButton>
                          </AForm.Item>
                        </Empty>
                      )}
                    </div>
                    {/* <pre>{JSON.stringify(values, 0, 2)}</pre> */}
                  </AForm>
                </LoadingSpinner>
              </div>
            );
          }}
        />
      )}
    </MakeAsyncFunction>
  );
};

CreateQuestionSetForm.propTypes = {
  isLoading: PropTypes.bool
};

CreateQuestionSetForm.defaultProps = {
  isLoading: false
};
